import { useCallback } from "react";

/**
 * The cue a scrolling body gives that more lies below its foot (P1.9-T196). A body that scrolls cuts a row through at
 * its edge and nothing else says there is more, so a body that uses `useScrollCue` carries `data-more="true"` while
 * content lies below it and the kit's `.ot-scroll[data-more="true"]` rule fades its foot. The state is the element's
 * attribute and never React state, so a scroll re-renders nothing, and at rest nothing listens for a timer or a frame.
 */

/**
 * What the cue reads of a scrolling box.
 */
export interface ScrollMeasure {
  readonly scrollTop: number;
  readonly clientHeight: number;
  readonly scrollHeight: number;
}

/**
 * Whether content lies below a scrolling box's foot: a pixel or more of it, so the fraction a zoomed page leaves at
 * the end of a scroll does not count.
 *
 * @param measure The box's scroll position and sizes.
 * @returns True while there is more to scroll down to.
 */
export function moreBelow(measure: ScrollMeasure): boolean {
  return measure.scrollHeight - measure.clientHeight - measure.scrollTop >= 1;
}

/**
 * Writes the cue on an element as it stands.
 *
 * @param element The scrolling box.
 */
function mark(element: HTMLElement): void {
  element.dataset["more"] = moreBelow(element) ? "true" : "false";
}

/**
 * Keeps `data-more` on a scrolling box true while content lies below its foot: read as the box mounts, as it scrolls,
 * and when the box or any child of it changes size or the children change (a section chosen, a row shown or hidden).
 * Where the page has no `ResizeObserver` the cue is read on mount and on scroll alone.
 *
 * @returns A ref callback for the scrolling box; it stops everything it started as the box leaves the page.
 * @example
 * const cue = useScrollCue();
 * return <div ref={cue} className="ot-scroll">{rows}</div>;
 */
export function useScrollCue(): (element: HTMLElement | null) => (() => void) | void {
  return useCallback((element: HTMLElement | null) => {
    if (element === null) {
      return;
    }
    const read = (): void => mark(element);
    read();
    element.addEventListener("scroll", read, { passive: true });
    const resize = typeof ResizeObserver === "function" ? new ResizeObserver(read) : null;
    const watch = (): void => {
      if (resize === null) {
        return;
      }
      resize.disconnect();
      resize.observe(element);
      for (const child of Array.from(element.children)) {
        resize.observe(child);
      }
    };
    watch();
    const change =
      typeof MutationObserver === "function"
        ? new MutationObserver(() => {
            watch();
            read();
          })
        : null;
    change?.observe(element, { childList: true });
    return () => {
      element.removeEventListener("scroll", read);
      resize?.disconnect();
      change?.disconnect();
    };
  }, []);
}
